import React from 'react';
import { Link } from '@inertiajs/react';
import { Card } from '~/marketing/ui';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { LegalPage, Section, P, UL } from '~/marketing/LegalPage';
import { legal } from '~/marketing/legalData';
import { routes } from '~/marketing/routes';
import { quoted } from '~/marketing/marketingData';

/* This is the page that needed rewriting rather than lifting, and one section
   of `landing/refund.html` had to go for a reason worth recording.

   **"One-off services — fixed-scope engagements ... deliverables already
   provided"** described consulting work. Managed Payments excludes
   "professional services, such as consulting, marketing, design, development,
   or tech support", and a refund policy describing fixed-scope engagements
   tells a reviewer we sell exactly that. It is the single most dangerous
   paragraph the placeholder site carried, and it describes work this business
   does not do — the fix engine stays advice rather than done-for-you work
   precisely so this stays true.

   **The "14-day money-back guarantee" was never the policy.** The trial is 14
   days with no card, which is a stronger offer and a different one: you decide
   before paying rather than paying and asking for it back. Carrying both would
   have promised 28 days nobody decided on.

   **Annual had no rule at all**, while the decision of 2026-09-20 has a precise
   one — prorate at the monthly rate, absorb the fees, nothing left after month
   ten. Leaving it out is how somebody cancelling an annual plan in month two
   finds out what they are owed by arguing about it.

   The month-ten crossover is stated here because it is the kind of rule that
   reads as a trick when met unannounced. The in-product cancellation screen
   still needs its own sanctioned wording in `copy-rules.md`; this is the legal
   statement of the policy, not that string. */

export default function Refund() {
  return (
    <LegalPage
      title="Refund &amp; Cancellation Policy"
      path={routes.refund}
      description="How the trial, canceling and refunds work, including how an annual plan canceled early is refunded."
    >
      {/* No lede since 2026-10-26, on the founder's review. It read "…including the
          one rule about annual plans that is worth reading before you buy one
          rather than after you cancel", a warning nobody needed, and without it
          the sentence only repeated the title; the short version below says what
          the page covers. The search description keeps a neutral form of it. */}
      <Card variant="signal" pad="lg">
        <P>
          <strong>Short version:</strong> the trial is 14 days and takes no credit card, so you can decide
          before you pay anything. Cancel any time and keep access to the end of the period you
          paid for. When you cancel, everything you collected stays readable for twelve months
          after the plan ends. Cancel an annual plan early and you are refunded what you
          paid, less the months you used at the monthly price.
        </P>
      </Card>

      <Section title="1. The trial">
        <P>
          A new account can start with a 14-day free trial, or buy a plan right away. During a
          trial you can choose a plan at any time, and buying one ends the trial. The trial takes no
          credit card, so nothing is charged until you choose a plan. If the trial ends before you
          choose one, collection stops until you do.
        </P>
      </Section>

      <Section title="2. Canceling">
        <P>
          Cancel at any time from your Plan page, or by emailing {legal.supportEmail}.
          Cancellation takes effect at the end of your current billing period:
        </P>
        <UL>
          <li>You keep full access until that period ends.</li>
          <li>You are not charged again.</li>
          <li>Canceling is free.</li>
          <li>
            <strong>What stays.</strong> Your businesses, questions and every answer collected for
            them stay exactly as they are; canceling only stops collection.
          </li>
          {/*
            Read rather than restated, 2026-10-09 with item 17. This bullet used
            to compress the string's first two sentences into its bold lead and
            then copy the third verbatim — prose that happens to be word for word
            identical, which is a second copy wearing a first copy's clothes.

            The bold is a Class 1 lead-in label now, which is this list's own
            convention: the bullet above reads "What stays." the same way. It keeps the bullet scannable for somebody
            skimming a cancellation page without bolding a fragment of a
            sanctioned string, which would be slicing one in half.

            The label names the topic rather than restating the window. "After
            the plan ends" was the first draft and mis-scopes: the string opens
            with what happens *while* the plan runs, so the lead announced a
            narrower subject than the sentence under it. A label carrying the
            twelve months would be worse again — the decision of 2026-10-09 put
            that figure in one string precisely so two copies of it cannot drift.
          */}
          <li>
            <strong>What happens to your data.</strong> {quoted.retention} Your account is kept
            until you delete it.
          </li>
        </UL>
        <P>
          Canceling stops future charges; a refund returns money already paid.
        </P>
      </Section>

      <Section title="3. Monthly plans">
        <P>
          Monthly subscriptions are billed in advance and are not refunded for a partial month,
          because canceling already stops the next charge and you keep the period you paid for.
          The exceptions in section 5 still apply.
        </P>
      </Section>

      <Section title="4. Annual plans">
        <P>
          An annual plan gives twelve months for the price of ten. If you cancel one before the year is up, we
          refund what you paid less the months you have used,{' '}
          <strong>charged at that plan&rsquo;s monthly price</strong> — not at one twelfth of the
          annual price.
        </P>
        <P>
          On the $79 plan, an annual subscription is $790. Cancel in month two and two months have
          been used at $79, so $158 is retained and <strong>$632 is refunded</strong>.
        </P>
        <P>
          <strong>That arithmetic reaches zero at month ten</strong>, because ten months at the
          monthly price is the whole annual price. After month ten an annual plan has no refund
          left. The two free months are what you get for staying the year.
        </P>
        <P>
          <strong>Your refund is the full amount.</strong> We absorb any processing and
          merchant-of-record fees.
        </P>
      </Section>

      <Section title="5. Other refunds we give">
        <UL>
          <li>
            <strong>Service failure.</strong> If a significant part of the Service was unavailable
            or not working for an extended period during a billing period, we refund or credit that
            period pro rata.
          </li>
          <li>
            <strong>Billing error.</strong> If you were charged incorrectly, charged after
            canceling, or charged more than once, we refund the incorrect amount in full.
          </li>
          <li>
            <strong>Accidental renewal.</strong> If a subscription renewed unintentionally and you
            have not meaningfully used the Service in the new period, contact us within 14 days of
            the charge and we will refund it.
          </li>
          <li>
            <strong>Discontinuation.</strong> If we discontinue the Service, we refund any prepaid
            period you cannot use, pro rata.
          </li>
        </UL>
      </Section>

      <Section title="6. The free check">
        <P>
          The free check costs nothing and is separate from any plan, so there is nothing to refund.
        </P>
      </Section>

      <Section title="7. How to request a refund">
        <P>
          Email <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a> from the address on
          the account, saying which charge you would like refunded. We aim to reply within two
          business days.
        </P>
        <P>
          Because {legal.mor} is the merchant of record for purchases of the Service, approved
          refunds are processed by {legal.mor} back to your original payment method and typically
          appear within 5–10 business days depending on your bank. {legal.mor} may also issue a
          refund directly in some circumstances, and their buyer terms apply alongside this policy.
        </P>
      </Section>

      <Section title="8. Chargebacks">
        <P>
          If a charge looks wrong, email us first: we can usually put it right faster than a bank
          dispute.
        </P>
      </Section>

      <Section title="9. Your statutory rights">
        <P>
          This policy is in addition to, and does not limit, any refund or cancellation rights you
          have under applicable consumer law, including the statutory right of withdrawal available
          to consumers in the UK and EU. Where {legal.mor} applies a regional cooling-off period as
          merchant of record, that applies too.
        </P>
      </Section>

      <Section title="10. Related">
        <P>
          <Link href={routes.terms}>Terms of Service</Link> ·{' '}
          <Link href={routes.privacy}>Privacy Policy</Link> ·{' '}
          <Link href={routes.contact}>Contact</Link>
        </P>
      </Section>
    </LegalPage>
  );
}

Refund.layout = withMarketingLayout;
